import { Box, Chip, Flex, spaces, Text } from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { Group, GroupMembershipType } from '@/types/groups';

type ReadOnlyGroupChipsProps = {
  groups: Group[];
};

// Displays an employee's dynamic (rule-derived) and HRIS-synced groups as
// non-removable chips, since membership in both is managed automatically
// rather than by hand. Pair with `useReadOnlyGroups`/`useManageableGroups`
// and pass the same `groups` as `lockedGroups` to `EmployeeGroupSelector`.
const ReadOnlyGroupChips = ({ groups }: ReadOnlyGroupChipsProps) => {
  const { t } = useTranslation('common');

  if (groups.length === 0) return null;

  return (
    <Box mt={spaces.space_xs} mb={spaces.space_l}>
      <Text variant="body-2" intent="secondary">
        {t('readOnlyGroups.caption')}
      </Text>
      <Flex
        alignItems="center"
        gap={spaces.space_xs}
        mt={spaces.space_xs}
        flexWrap="wrap">
        {groups.map(group => {
          const isDynamicGroup =
            group.membershipType === GroupMembershipType.Dynamic;
          return (
            <span
              key={group.id}
              title={t(
                isDynamicGroup
                  ? 'readOnlyGroups.dynamicTooltip'
                  : 'readOnlyGroups.hrisTooltip'
              )}>
              <Chip
                id={`readonly-group-chip-${group.id}`}
                variant="small"
                icon={isDynamicGroup ? 'bolt' : 'lock'}>
                {group.name}
              </Chip>
            </span>
          );
        })}
      </Flex>
    </Box>
  );
};

export default ReadOnlyGroupChips;
